import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  useTheme,
  alpha,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { config } from "../config/config";
import { useNavigate } from "../url";
import { useAuthenticationToken } from "../context/AuthenticationTokenContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";
import { faSprayCanSparkles } from "@fortawesome/pro-solid-svg-icons";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import * as d from "shared/src/date.mjs";
import { useLoaderData, useLocation } from "react-router";
import * as api from "../services/api";
import { getCompanyByHostname } from "shared/src/company.mjs";
import AutocompleteSelect from "../components/AutocompleteSelect";

const AUTH_TOKEN_KEY = "labs1503.auth.auth_token";
const LAST_APPOINTMENT_ID = "lastAppointmentId";

function useUpdateChair() {
  const location = useLocation();
  const chair = new URLSearchParams(location.search).get("chair");

  React.useEffect(() => {
    if (chair == null) {
      return;
    }

    localStorage.setItem("currentChairId", chair);
  }, [chair]);
}

function useShowChairSelector() {
  const location = useLocation();
  const chair = new URLSearchParams(location.search).get("chair");

  return config.env !== "production" && chair == null;
}

export function loader() {
  return api.loadRfidAuthData(getCompanyByHostname(location.hostname));
}

export default function RFIDAuth() {
  const { calendars } = useLoaderData();
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [_, setAuthToken] = useAuthenticationToken();
  useUpdateChair();
  const showChairSelector = useShowChairSelector();
  const currentChairId = localStorage.getItem("currentChairId");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [isTypingPin, setIsTypingPin] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const rfidInputRef = useRef(null);
  const pinInputRef = useRef(null);

  const expectedRFIDLength = 10;
  const expectedPINLength = 6;

  const focusRfidInputRef = useRef(true);

  const isCleaningLogin = useMemo(
    () => Boolean(localStorage.getItem(LAST_APPOINTMENT_ID)),
    [],
  );

  useEffect(() => {
    const handleRFIDInput = (event) => {
      if (isTypingPin) return;

      const input = event.target.value.trim();
      if (!input) return;

      if (input.length === expectedRFIDLength) {
        sendToBackend(input, "");
        event.target.value = "";
      }
    };

    const focusRFIDInput = () => {
      if (
        focusRfidInputRef.current &&
        rfidInputRef.current &&
        document.activeElement !== pinInputRef.current &&
        !isTypingPin
      ) {
        rfidInputRef.current.focus();
      }
    };

    const rfidInput = rfidInputRef.current;
    if (rfidInput) {
      rfidInput.addEventListener("input", handleRFIDInput);
      focusRFIDInput();
    }

    const interval = setInterval(focusRFIDInput, 500);
    return () => {
      if (rfidInput) {
        rfidInput.removeEventListener("input", handleRFIDInput);
      }
      clearInterval(interval);
    };
  }, [isTypingPin]);

  const handlePinChange = (event) => {
    const input = event.target.value
      .replace(/\D/g, "")
      .slice(0, expectedPINLength);
    setPin(input);
    setIsTypingPin(input.length > 0);
  };

  const handleSubmit = () => {
    if (pin.length === expectedPINLength) {
      sendToBackend("", pin);
    }
  };

  const sendToBackend = async (rfid, pin) => {
    if (!rfid && pin.length !== expectedPINLength) return;

    setLoading(true);
    setError("");

    const lastAppointmentId = localStorage.getItem(LAST_APPOINTMENT_ID);
    const currentChairId = localStorage.getItem("currentChairId");

    try {
      const response = await fetch(`${config.apiUrl}/authenticate-rfid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rfid,
          pin,
          lastAppointmentId,
          currentChairId,
          day: d.toDateString(new Date()),
        }),
      });

      const data = await response.json();

      if (data.chair_id) {
        localStorage.setItem("currentChairId", data.chair_id);
      }

      if (response.ok) {
        localStorage.setItem(AUTH_TOKEN_KEY, data.token);
        setAuthToken(data.token);

        if (data.employee?.location_id) {
          localStorage.setItem("currentLocationId", data.employee.location_id);
        }

        if (data.chair_id) {
          localStorage.setItem("currentChairId", data.chair_id);
        }

        const redirectUrl = data?.appointment?.redirect_url || "";
        const apptId = data?.appointment?.appointment_id || null;

        if (
          redirectUrl.includes("/cleaning") &&
          apptId &&
          !localStorage.getItem(LAST_APPOINTMENT_ID)
        ) {
          localStorage.setItem(LAST_APPOINTMENT_ID, apptId);
        }

        if (!redirectUrl.includes("/cleaning")) {
          localStorage.removeItem(LAST_APPOINTMENT_ID);
        }

        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          setError(
            t(
              "auth.noAppointment",
              "Nejste přiřazeni k žádnému aktivnímu zákroku. Kontaktujte prosím Floor Managera.",
            ),
          );
        }
      } else {
        setError(data.error || t("auth.failed", "Přihlášení selhalo."));
      }
    } catch (err) {
      console.error("Error sending credentials:", err);
      setError(t("auth.networkError", "Síťová chyba. Zkuste to prosím znovu."));
    } finally {
      setLoading(false);
      setPin("");
    }
  };

  return (
    <>
      <input
        ref={rfidInputRef}
        id="rfidHiddenInput"
        type="text"
        style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
        autoComplete="new-password"
      />

      <Typography
        variant="h1"
        sx={{ fontWeight: "bold", mb: 1, fontSize: "3.6rem" }}
      >
        {isCleaningLogin
          ? t("cleaning.authTitle", {
              seatName: localStorage.getItem("currentChairName"),
            })
          : t("auth.title", "Přihlášení do systému")}{" "}
        {isCleaningLogin && (
          <FontAwesomeIcon
            style={{ color: alpha(theme.palette.primary.main, 0.2) }}
            icon={faSprayCanSparkles}
          />
        )}
      </Typography>

      {showChairSelector && (
        <Box sx={{ my: 2 }}>
          <AutocompleteSelect
            label="Křeslo"
            options={calendars}
            defaultValue={calendars.find((c) => c.id === currentChairId)}
            disableClearable
            onFocus={() => (focusRfidInputRef.current = false)}
            onChange={(o) => {
              const newChair = o?.id;
              focusRfidInputRef.current = true;
              if (newChair != null) {
                localStorage.setItem("currentChairId", o.id);
              }
            }}
            onBlur={() => (focusRfidInputRef.current = true)}
          />
        </Box>
      )}

      {isCleaningLogin && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid",
            borderColor: "error.main",
            backgroundColor: "#ffecef",
            color: "error.main",
            fontWeight: "600",
            borderRadius: 1,
            px: 1,
            py: 1.5,
            my: 4,
            gap: 0.5,
          }}
        >
          <FontAwesomeIcon size="2x" icon={faTriangleExclamation} />
          {t("cleaning.cleaningRequiredAlert")}
        </Box>
      )}

      <Grid container spacing={1} sx={{ mb: 4 }}>
        <Grid size="grow" xs>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography fontWeight="600">
              {t("auth.cardInstruction", "Načtěte kartu")}
            </Typography>
            <img
              src="/img/rfid_login.svg"
              width="44"
              height="39"
              alt="RFID login"
            />
          </Box>
        </Grid>

        <Grid size="grow" sx={{ alignSelf: "flex-end", mb: 1 }}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            {t("auth.or", "nebo")}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 7 }}>
          <TextField
            inputRef={pinInputRef}
            value={pin}
            onChange={handlePinChange}
            fullWidth
            variant="outlined"
            label={t("auth.pinLabel", "Napište 6 místný kód")}
            type={showPin ? "text" : "password"}
            sx={{
              "& input": {
                textAlign: "center",
                fontSize: "1.2rem",
                letterSpacing: "0.3rem",
              },
              "& .MuiFormLabel-root": {
                fontWeight: 600,
              },
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={
                        showPin
                          ? t("auth.hidePin", "Skrýt kód")
                          : t("auth.showPin", "Zobrazit kód")
                      }
                      onClick={() => setShowPin((s) => !s)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      size="small"
                    >
                      {showPin ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
                inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
              },
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pin.length === expectedPINLength) {
                handleSubmit();
              }
            }}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        sx={{ width: "100%" }}
        onClick={handleSubmit}
        disabled={loading || pin.length !== expectedPINLength}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          t("auth.submit", "Přihlásit se")
        )}
      </Button>

      {error && (
        <Typography color="error" sx={{ marginTop: 2 }}>
          {error}
        </Typography>
      )}
    </>
  );
}
